import React, { useState, useEffect } from 'react';
import StandardModal from '../../common/modals/StandardModal';
import { Button, ButtonGroup, Icon } from '../../common';
import { Select } from '../../common';
import SubscriptionRequestsService from '../../../services/SubscriptionRequestsService';
import { AzureResourceModel } from '../../../models/viewModels/Subscriptions';

export interface ResourceGroupEntry {
	resourceGroupId: string;
	resourceGroupName: string;
	resourceId?: string;
	resourceName?: string;
	expiration: string;
}

export const EXPIRATION_OPTIONS = [
	{ value: '28', text: 'Temporary (28 days)' },
	{ value: '60', text: '60 days' },
	{ value: '90', text: '90 days' },
	{ value: '180', text: '6 months' },
	{ value: '270', text: '9 months' },
	{ value: '365', text: '12 months' },
];

interface AddResourceGroupModalProps {
	visible: boolean;
	subscriptionId: number | null;
	preselectedExpiration?: string;
	cachedRGs?: AzureResourceModel[];
	cachedResources?: Record<string, AzureResourceModel[]>;
	existingEntries?: ResourceGroupEntry[];
	onRGsLoaded?: (rgs: AzureResourceModel[]) => void;
	onResourcesLoaded?: (rgId: string, resources: AzureResourceModel[]) => void;
	onConfirm: (entry: ResourceGroupEntry) => void;
	onCancel: () => void;
}

const truncate = (text: string | undefined, max = 45): string =>
	text?.length > max ? `${text.slice(0, max)}…` : text ?? '';

const AddResourceGroupModal = ({
	visible,
	subscriptionId,
	preselectedExpiration,
	cachedRGs,
	cachedResources,
	existingEntries = [],
	onRGsLoaded,
	onResourcesLoaded,
	onConfirm,
	onCancel,
}: AddResourceGroupModalProps) => {
	const [subscriptionRequestsService] = useState(() => new SubscriptionRequestsService());

	const [resourceGroups, setResourceGroups] = useState<AzureResourceModel[]>([]);
	const [resources, setResources] = useState<AzureResourceModel[]>([]);
	const [loadingRGs, setLoadingRGs] = useState(false);
	const [loadingResources, setLoadingResources] = useState(false);

	const [selectedRGId, setSelectedRGId] = useState('');
	const [selectedResourceId, setSelectedResourceId] = useState('');
	const [selectedExpiration, setSelectedExpiration] = useState('');

	useEffect(() => {
		if (!visible || subscriptionId == null) return;

		setSelectedRGId('');
		setSelectedResourceId('');
		setSelectedExpiration(preselectedExpiration ?? '');
		setResources([]);

		if (cachedRGs && cachedRGs.length > 0) {
			setResourceGroups(cachedRGs);
			return;
		}

		setLoadingRGs(true);
		subscriptionRequestsService
			.getAllResourceGroupsInSubscription(subscriptionId)
			.then((rgs) => {
				setResourceGroups(rgs);
				onRGsLoaded?.(rgs);
			})
			.finally(() => setLoadingRGs(false));
	}, [visible, subscriptionId]);

	const handleRGChange = (value: string) => {
		setSelectedRGId(value);
		setSelectedResourceId('');
		setResources([]);

		if (!value || subscriptionId == null) return;

		const cached = cachedResources?.[value];
		if (cached) {
			setResources(cached);
			return;
		}

		setLoadingResources(true);
		subscriptionRequestsService
			.getAllResourcesInResourceGroup(subscriptionId, value)
			.then((res) => {
				setResources(res);
				onResourcesLoaded?.(value, res);
			})
			.finally(() => setLoadingResources(false));
	};

	const isDuplicate = selectedRGId
		? existingEntries.some(
			(e) => e.resourceGroupId === selectedRGId &&
				(e.resourceId ?? '') === selectedResourceId
		  )
		: false;

	const isValid = selectedRGId && selectedExpiration && !isDuplicate;

	const handleConfirm = () => {
		const rg = resourceGroups.find((r) => r.id === selectedRGId);
		const resource = resources.find((r) => r.id === selectedResourceId);
		const expirationLabel = EXPIRATION_OPTIONS.find((o) => o.value === selectedExpiration)?.text ?? selectedExpiration;

		onConfirm({
			resourceGroupId: selectedRGId,
			resourceGroupName: rg?.name ?? selectedRGId,
			...(selectedResourceId ? { resourceId: selectedResourceId, resourceName: resource?.name ?? selectedResourceId } : {}),
			expiration: expirationLabel,
		});

		setSelectedRGId('');
		setSelectedResourceId('');
		setSelectedExpiration('');
	};

	const rgOptions = resourceGroups.map((rg) => ({ value: rg.id, text: truncate(rg.name) }));
	const resourceOptions = resources.map((r) => ({ value: r.id, text: truncate(r.name) }));

	return (
		<StandardModal
			show={visible}
			onDismiss={onCancel}
			title="Add Resource Group & Resource"
			footer={
				<ButtonGroup>
					<Button
						variant="primary"
						size="small"
						disabled={!isValid}
						onClick={handleConfirm}
					>
						<Icon icon="plus" size="small" />
						<span>Add</span>
					</Button>
					<Button
						variant="secondary"
						size="small"
						onClick={onCancel}
					>
						<span>Cancel</span>
					</Button>
				</ButtonGroup>
			}
		>
			<Select
				label="Resource Group"
				value={selectedRGId}
				onChange={(e) => handleRGChange(e.target.value)}
				options={rgOptions}
				isLoading={loadingRGs}
				disabled={loadingRGs}
			/>
			<Select
				label="Resource (optional)"
				value={selectedResourceId}
				onChange={(e) => setSelectedResourceId(e.target.value)}
				options={[{ value: '', text: 'No specific resource (RG level)' }, ...resourceOptions]}
				isLoading={loadingResources}
				disabled={!selectedRGId || loadingResources}
			/>
			{isDuplicate && (
				<p style={{ color: 'red', margin: '0.25rem 0 0' }}>
					This resource group / resource combination has already been added.
				</p>
			)}
			<Select
				label="Expiration"
				value={selectedExpiration}
				onChange={(e) => setSelectedExpiration(e.target.value)}
				options={EXPIRATION_OPTIONS}
				disabled={!!preselectedExpiration}
			/>
		</StandardModal>
	);
};

export default AddResourceGroupModal;
